import React, { useEffect, useState } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, CardHeader } from "reactstrap";
import Loading from "../components/Loading";
import { apiCalls, downloadPlugin, readStores, SWAGGER_URL } from "../utils/stores";

function save(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const StoreComponent = () => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [store, setStore] = useState(undefined);
  const [shopKey, setShopKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getAccessTokenSilently()
      .then(readStores)
      .then((stores) => setStore(stores.find((s) => String(s.id) === id) || null))
      .catch((e) => setError(e.message || "Could not load the store"));
  }, [getAccessTokenSilently, id]);

  const onDownload = async () => {
    setError("");
    setBusy(true);
    try {
      const plugin = await downloadPlugin(store.id, await getAccessTokenSilently());
      setShopKey(plugin.shopKey);
      save(plugin.zip, "kuzfit-wordpress-plugin.zip");
    } catch (e) {
      setError(e.message || "Could not download the plugin");
    } finally {
      setBusy(false);
    }
  };

  if (error && store === undefined) return <Alert color="danger">{error}</Alert>;
  if (store === undefined) return <Loading />;
  if (store === null) return (
    <div className="text-center py-5">
      <h5>Store not found</h5>
      <Link to="/stores">Back to your stores</Link>
    </div>
  );

  return (
    <>
      <Link to="/stores">&larr; Your stores</Link>
      <h1 className="mt-2">{store.name}</h1>
      <p className="text-muted">{store.domain}</p>
      {error && <Alert color="danger">{error}</Alert>}

      <Card className="mb-4">
        <CardHeader><h5 className="mb-0">WordPress plugin</h5></CardHeader>
        <CardBody>
          <p>The KuzFit plugin for WooCommerce, set up for this store: install it and it is connected, nothing to paste.</p>
          <ol>
            <li>Download the plugin.</li>
            <li>In WordPress, go to Plugins &rarr; Add New &rarr; Upload Plugin, choose the zip and activate it.</li>
            <li>Your product images get a "Try it on" button.</li>
          </ol>
          <Button color="primary" onClick={onDownload} disabled={busy}>
            <i className="fas fa-download me-2"></i>{busy ? "Preparing..." : "Download plugin"}
          </Button>
          <p className="text-muted small mt-2 mb-0">
            Each download comes with a new shop key, and replaces the key in any plugin installed before.
          </p>
          {shopKey && (
            <p className="mt-3 mb-0">Shop key: <code>{shopKey}</code></p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h5 className="mb-0">API calls</h5></CardHeader>
        <CardBody>
          <p>
            What the plugin calls, for a shopper logged in to KuzFit.
            {!shopKey && " Download the plugin to see them with this store's key."}
          </p>
          {apiCalls(shopKey || "{shop key}").map((c) => (
            <div key={c.name} className="mb-3">
              <h6>{c.name}</h6>
              <pre className="bg-light p-2 mb-1" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{c.method} {c.url}</pre>
              <small className="text-muted">{c.returns}</small>
            </div>
          ))}
          <a href={SWAGGER_URL} target="_blank" rel="noopener noreferrer">All KuzFit API calls (Swagger)</a>
        </CardBody>
      </Card>
    </>
  );
};

export default withAuthenticationRequired(StoreComponent, { onRedirecting: () => <Loading /> });
